/**
 * WarehouseOptimizer.jsx — Phase 5 · AI Layout Optimiser.
 *
 * UI lifecycle:
 *   1. Operator opens the page for a warehouse.
 *   2. Adjusts optional constraints (top_n, days, FEFO/weight/affinity toggles).
 *   3. Clicks "Run Optimiser". The backend returns a ranked list of moves,
 *      each with SKU, current tile, proposed tile, distance saved, and
 *      projected labour hours saved per week.
 *   4. Operator ticks the moves they want to accept.
 *   5. Clicks "Apply as scenario" — creates a Phase-4 scenario doc with the
 *      accepted moves so the Compare table can quantify the delta before
 *      touching the live layout.
 *
 * Nothing here mutates the live layout directly — the safest possible path
 * for a production change.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Sparkle, ArrowRight, Play, MagicWand, ChartLineUp, Clock, Path, Package, PushPin, Barbell, ArrowsSplit, ArrowLeft, CheckSquare, Square,
} from '@phosphor-icons/react';
import {
  getWarehouses, runLayoutOptimizer, applyOptimizerMoves,
} from '../lib/api';

const fmt = (n) => (n == null ? '—' : Math.round(n).toLocaleString());
const fmt2 = (n) => (n == null ? '—' : (Math.round(n * 100) / 100).toLocaleString());

export default function WarehouseOptimizer() {
  const [params, setParams] = useSearchParams();
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWh, setSelectedWh] = useState(params.get('wh') || '');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);

  // Optimiser knobs
  const [topN, setTopN] = useState(50);
  const [pickDays, setPickDays] = useState(90);
  const [respectFefo, setRespectFefo] = useState(true);
  const [respectWeight, setRespectWeight] = useState(true);
  const [useAffinity, setUseAffinity] = useState(true);
  const [heavyKg, setHeavyKg] = useState(20);

  // Accepted move set (by sku_id) — controls which moves get promoted into
  // a scenario when the operator clicks Apply.
  const [accepted, setAccepted] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const r = await getWarehouses();
        const list = r.data || [];
        setWarehouses(list);
        if (!selectedWh) {
          const def = list.find((w) => w.is_default) || list[0];
          if (def) { setSelectedWh(def.id); setParams({ wh: def.id }); }
        }
      } catch (e) { toast.error('Failed to load warehouses'); }
    })();
  }, []);

  const moves = useMemo(
    () => (result?.recommendations || []).filter((r) => r.recommendation === 'move'),
    [result],
  );
  const keeps = useMemo(
    () => (result?.recommendations || []).filter((r) => r.recommendation === 'keep'),
    [result],
  );

  const acceptedMoves = useMemo(
    () => moves.filter((m) => accepted.has(m.sku_id)),
    [moves, accepted],
  );

  // When a run completes, pre-select every move — operators typically want
  // the recommended set as the default acceptance.
  useEffect(() => {
    if (result?.recommendations) {
      setAccepted(new Set((result.recommendations || []).filter((r) => r.recommendation === 'move').map((r) => r.sku_id)));
    }
  }, [result]);

  const handleRun = async () => {
    if (!selectedWh) return;
    setRunning(true); setResult(null);
    try {
      const r = await runLayoutOptimizer(selectedWh, {
        top_n: topN, pick_days: pickDays,
        respect_fefo: respectFefo, respect_weight: respectWeight, use_affinity: useAffinity,
        heavy_kg: heavyKg,
      });
      setResult(r.data);
      const n = (r.data.recommendations || []).filter((x) => x.recommendation === 'move').length;
      toast.success(`${n} move${n === 1 ? '' : 's'} recommended`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Optimiser failed');
    } finally { setRunning(false); }
  };

  const toggleAccept = (sku_id) => {
    const next = new Set(accepted);
    if (next.has(sku_id)) next.delete(sku_id); else next.add(sku_id);
    setAccepted(next);
  };
  const acceptAll = () => setAccepted(new Set(moves.map((m) => m.sku_id)));
  const acceptNone = () => setAccepted(new Set());

  const handleApply = async () => {
    if (!acceptedMoves.length) return;
    const name = window.prompt('Name for the proposed scenario', 'Optimiser proposal — ' + new Date().toLocaleDateString());
    if (!name) return;
    setApplying(true);
    try {
      const r = await applyOptimizerMoves(selectedWh, {
        name,
        moves: acceptedMoves.map((m) => ({ sku_id: m.sku_id, target_tile_id: m.proposed_location.tile_id })),
      });
      toast.success(`Scenario "${r.data.name}" created with ${r.data.move_count} move(s)`);
      // Navigate to Scenarios so operator can hit Compare immediately
      window.location.href = `/warehouse-layout/scenarios?wh=${selectedWh}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  return (
    <Layout>
      <div className="max-w-7xl space-y-5" data-testid="warehouse-optimizer-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="overline">Warehouse</p>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MagicWand size={22} weight="fill" className="text-brand" />
              AI Layout Optimiser
            </h1>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Ranks SKU-to-location moves that would reduce total picker walking distance using 90-day pick velocity,
              product affinity, weight/size constraints, and FIFO/FEFO pins. Accepted moves become a scenario for compare.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/warehouse-layout" className="text-xs text-brand hover:underline flex items-center gap-1">
              <ArrowLeft size={12} /> Designer
            </Link>
            <Link to={`/warehouse-layout/scenarios?wh=${selectedWh}`} className="text-xs text-brand hover:underline flex items-center gap-1">
              <ArrowsSplit size={12} /> Scenarios
            </Link>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Warehouse</Label>
              <Select value={selectedWh} onValueChange={(v) => { setSelectedWh(v); setParams({ wh: v }); setResult(null); }}>
                <SelectTrigger className="w-56" data-testid="optimizer-wh-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="border border-border bg-muted/20 p-4 rounded" data-testid="optimizer-controls">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Top-N SKUs</Label>
              <Input
                type="number" min={1} max={500} value={topN}
                onChange={(e) => setTopN(Math.max(1, Math.min(500, Number(e.target.value) || 50)))}
                className="mt-1 h-8 text-xs font-mono"
                data-testid="opt-top-n"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">History window</Label>
              <Select value={String(pickDays)} onValueChange={(v) => setPickDays(Number(v))}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="opt-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">365 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ToggleField label="FEFO / FIFO pins" icon={PushPin} checked={respectFefo} onChange={setRespectFefo} testid="opt-fefo" />
            <ToggleField label="Weight limits" icon={Barbell} checked={respectWeight} onChange={setRespectWeight} testid="opt-weight" />
            <ToggleField label="Product affinity" icon={Package} checked={useAffinity} onChange={setUseAffinity} testid="opt-affinity" />
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Heavy threshold (kg)</Label>
              <Input
                type="number" min={0} step={0.5} value={heavyKg}
                onChange={(e) => setHeavyKg(Math.max(0, Number(e.target.value) || 20))}
                disabled={!respectWeight}
                className="mt-1 h-8 text-xs font-mono"
                data-testid="opt-heavy-kg"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              onClick={handleRun}
              disabled={!selectedWh || running}
              className="bg-brand hover:bg-brand/90"
              data-testid="run-optimizer-btn"
            >
              <Sparkle size={14} className="mr-1" weight="fill" />
              {running ? 'Optimising…' : 'Run optimiser'}
            </Button>
          </div>
        </div>

        {/* Empty / issues states */}
        {result?.issues?.length > 0 && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 p-3">
            <strong>No recommendations produced:</strong>{' '}
            {result.issues.includes('no_pick_history') && 'No completed pick lists were found in this history window — widen the window or wait for more data.'}
          </div>
        )}

        {/* Summary hero */}
        {result && result.summary && result.summary.moves_recommended > 0 && (
          <div className="border-2 border-brand/40 bg-brand/5 p-5 rounded-lg" data-testid="optimizer-summary">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="overline text-brand">Estimated impact</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">
                  Moving <span className="text-brand tabular-nums">{fmt(result.summary.moves_recommended)}</span> SKUs
                  {' '}could save
                  {' '}<span className="text-brand tabular-nums">≈ {fmt2(result.summary.labour_hours_saved_weekly)} labour hours/week</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  ≈ {fmt(result.summary.labour_hours_saved_yearly)} hours/year · {fmt(result.summary.distance_saved_yearly_km)} km less walking · {fmt(result.summary.reduction_pct)}% travel reduction
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" onClick={acceptNone} disabled={accepted.size === 0}
                  data-testid="accept-none-btn"
                >
                  <Square size={12} className="mr-1" /> Deselect all
                </Button>
                <Button
                  variant="outline" onClick={acceptAll} disabled={accepted.size === moves.length}
                  data-testid="accept-all-btn"
                >
                  <CheckSquare size={12} className="mr-1" /> Select all ({moves.length})
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={applying || acceptedMoves.length === 0}
                  className="bg-brand hover:bg-brand/90"
                  data-testid="apply-scenario-btn"
                >
                  <ChartLineUp size={12} className="mr-1" />
                  {applying ? 'Applying…' : `Apply ${acceptedMoves.length} as scenario`}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations table */}
        {result && (
          <div className="border border-border overflow-x-auto" data-testid="recommendations-table">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2 w-10">Accept</th>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-right px-3 py-2">Picks (window)</th>
                  <th className="text-left px-3 py-2">Current tile</th>
                  <th className="text-left px-3 py-2">Proposed tile</th>
                  <th className="text-right px-3 py-2">Distance saved</th>
                  <th className="text-right px-3 py-2">Labour saved</th>
                </tr>
              </thead>
              <tbody>
                {moves.length === 0 && keeps.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Run the optimiser to see recommendations.</td></tr>
                )}
                {moves.map((m) => {
                  const isAccepted = accepted.has(m.sku_id);
                  return (
                    <tr key={m.sku_id} className={`border-t border-border hover:bg-muted/20 ${isAccepted ? 'bg-brand/5' : ''}`} data-testid={`rec-row-${m.sku_code}`}>
                      <td className="px-3 py-2">
                        <button
                          type="button" onClick={() => toggleAccept(m.sku_id)}
                          className="hover:opacity-70"
                          data-testid={`accept-${m.sku_code}`}
                        >
                          {isAccepted ? <CheckSquare size={16} weight="fill" className="text-brand" /> : <Square size={16} />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-mono font-bold">{m.sku_code}</p>
                        <p className="text-[10px] text-muted-foreground truncate max-w-[220px]" title={m.sku_name}>{m.sku_name}</p>
                        <div className="flex gap-1 mt-0.5">
                          {m.fefo && <Badge variant="outline" className="text-[9px] border-purple-300 text-purple-800">FEFO</Badge>}
                          {m.weight_kg >= 20 && <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-800">{m.weight_kg}kg</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono">{fmt(m.picks_90d)}</td>
                      <td className="px-3 py-2">
                        <p className="font-medium">{m.current_locations[0]?.tile_label || '—'}</p>
                        <p className="text-[10px] text-muted-foreground">{fmt2(m.current_avg_distance_m)}m from dispatch</p>
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium flex items-center gap-1 text-emerald-700"><ArrowRight size={11} />{m.proposed_location.tile_label}</p>
                        <p className="text-[10px] text-muted-foreground">{fmt2(m.proposed_location.distance_m)}m from dispatch</p>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono text-emerald-700 font-bold">
                        −{fmt2(m.distance_saved_per_pick_m)}m
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono text-emerald-700 font-bold">
                        {fmt2(m.labour_hours_saved_weekly)}h/wk
                      </td>
                    </tr>
                  );
                })}
                {keeps.length > 0 && (
                  <tr><td colSpan={7} className="bg-muted/60 px-3 py-1 text-[10px] uppercase tracking-widest text-muted-foreground">Kept in place ({keeps.length})</td></tr>
                )}
                {keeps.map((k) => (
                  <tr key={k.sku_id} className="border-t border-border opacity-70" data-testid={`keep-row-${k.sku_code}`}>
                    <td className="px-3 py-2 text-muted-foreground"><PushPin size={12} /></td>
                    <td className="px-3 py-2 font-mono">{k.sku_code}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-mono">{fmt(k.picks_90d)}</td>
                    <td className="px-3 py-2">{k.current_locations[0]?.tile_label || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground italic">{k.reason}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">—</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {result && (
          <p className="text-[10px] text-muted-foreground text-right">
            Generated {new Date(result.generated_at).toLocaleString()} · window {result.settings?.pick_days}d · top-{result.settings?.top_n}
          </p>
        )}
      </div>
    </Layout>
  );
}

const ToggleField = ({ label, icon: Icon, checked, onChange, testid }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
      <Icon size={11} /> {label}
    </Label>
    <div className="mt-2 flex items-center gap-2">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      <span className="text-[10px] text-muted-foreground">{checked ? 'On' : 'Off'}</span>
    </div>
  </div>
);
